import PropTypes from 'prop-types';
import React from 'react';
import {
  PhoneInputContainer,
  Label,
  InputWrapper,
  CountrySelect,
  PhoneNumberInput,
} from './StyledComponents';

const COUNTRIES = [
  { code: '+1', flag: '🇺🇸', name: 'United States' },
  { code: '+44', flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: '+86', flag: '🇨🇳', name: 'China' },
  { code: '+33', flag: '🇫🇷', name: 'France' },
  { code: '+49', flag: '🇩🇪', name: 'Germany' },
  { code: '+91', flag: '🇮🇳', name: 'India' },
  { code: '+81', flag: '🇯🇵', name: 'Japan' },
  { code: '+52', flag: '🇲🇽', name: 'Mexico' },
  { code: '+234', flag: '🇳🇬', name: 'Nigeria' },
  { code: '+7', flag: '🇷🇺', name: 'Russia' },
  { code: '+82', flag: '🇰🇷', name: 'South Korea' },
  { code: '+34', flag: '🇪🇸', name: 'Spain' },
  { code: '+971', flag: '🇦🇪', name: 'United Arab Emirates' },
];

// Phone formatting helper
const formatPhoneNumber = (value, countryCode) => {
  const cleaned = value.replace(/\D/g, '');

  if (countryCode === '+1') {
    // US format: (555) 123-4567
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6,
      10
    )}`;
  }

  // Default format for other countries: space every 3-4 digits
  return cleaned.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
};

// Placeholder helper
const getPhonePlaceholder = (countryCode) => {
  switch (countryCode) {
    case '+1':
      return '(555) 123-4567';
    case '+44':
      return '7700 900000';
    case '+61':
      return '0412 345 678';
    case '+86':
      return '138 0000 0000';
    case '+33':
      return '06 12 34 56 78';
    case '+49':
      return '0151 23456789';
    case '+91':
      return '98765 43210';
    case '+81':
      return '090-1234-5678';
    case '+52':
      return '55 1234 5678';
    case '+234':
      return '0802 123 4567';
    case '+7':
      return '912 345-67-89';
    case '+82':
      return '010-1234-5678';
    case '+34':
      return '612 34 56 78';
    case '+971':
      return '050 123 4567';
    default:
      return 'Enter phone number';
  }
};

/**
 * PhoneInput - A reusable phone input component with country code selector
 *
 * @param {string} label - Label text for the input
 * @param {string} value - The phone number value
 * @param {Function} onChange - Callback when phone number changes
 * @param {string} countryCode - Selected country code (e.g., '+1')
 * @param {Function} onCountryChange - Callback when country code changes
 * @param {string} placeholder - Optional custom placeholder
 */
const PhoneInput = ({
  label = 'Phone Number',
  value,
  onChange,
  countryCode = '+1',
  onCountryChange,
  placeholder,
  ...props
}) => {
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value, countryCode);
    onChange(formatted);
  };

  const handleCountryChange = (e) => {
    onCountryChange(e.target.value);
  };

  return (
    <PhoneInputContainer>
      {label && <Label>{label}</Label>}
      <InputWrapper>
        <CountrySelect value={countryCode} onChange={handleCountryChange}>
          {COUNTRIES.map((country) => (
            <option key={country.code} value={country.code}>
              {country.flag} {country.code}
            </option>
          ))}
        </CountrySelect>
        <PhoneNumberInput
          type="tel"
          placeholder={placeholder || getPhonePlaceholder(countryCode)}
          value={value}
          onChange={handlePhoneChange}
          {...props}
        />
      </InputWrapper>
    </PhoneInputContainer>
  );
};

PhoneInput.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  countryCode: PropTypes.string,
  onCountryChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export default PhoneInput;
